import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { ArrowLeft, BookOpen, ChevronRight, Tag, Trophy, Eye, Shield, TrendingUp, Users, MapPin, Zap } from "lucide-react";

const RaceLibraryPage = ({ user, token, onLogout }) => {
  const navigate = useNavigate();
  const [races, setRaces] = useState([]);
  const [selectedRace, setSelectedRace] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const authHeaders = { Authorization: `Bearer ${token}` };

  // Fetch library index
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/library/races`, { headers: authHeaders });
        setRaces(res.data.races || []);
      } catch (e) { /* silent */ }
      setLoading(false);
    };
    load();
  }, []);

  // Fetch full detail when a race is selected
  const loadDetail = useCallback(async (raceId) => {
    setSelectedRace(raceId);
    setDetail(null);
    try {
      const res = await axios.get(`${API}/library/races/${raceId}`, { headers: authHeaders });
      setDetail(res.data);
    } catch (e) { /* silent */ }
  }, []);

  return (
    <div className="min-h-screen bg-[#090a0d] text-white flex flex-col" data-testid="race-library-page">
      {/* Header */}
      <header className="bg-[#0d0f12]/95 backdrop-blur-sm border-b border-white/10 px-4 h-14 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-white/40 hover:text-white transition-colors" data-testid="library-back-hub">
            <BookOpen className="w-4 h-4 text-[#F59E0B]" />
            <span className="font-heading text-sm font-bold tracking-wider text-white uppercase">Race Library</span>
          </button>
          <span className="text-[#F59E0B] text-xs font-bold">The Intelligence Vault</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-white/30 text-xs font-mono">{races.length} races stored</span>
          <Button onClick={() => navigate("/")} variant="ghost" size="sm" className="text-white/40 hover:text-white h-8" data-testid="library-hub-btn">
            <ArrowLeft className="w-3 h-3 mr-1" /> Hub
          </Button>
        </div>
      </header>

      <main className="flex-1 flex overflow-hidden">
        {/* Left panel — Race list */}
        <div className="w-[360px] border-r border-white/10 flex-shrink-0 overflow-y-auto">
          <div className="p-3">
            <div className="text-white/30 text-[10px] uppercase tracking-widest font-bold mb-3 px-1">Completed Races</div>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-[#F59E0B] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="space-y-2">
                {races.map((race) => (
                  <RaceCaseCard
                    key={race.race_id}
                    race={race}
                    selected={selectedRace === race.race_id}
                    onClick={() => loadDetail(race.race_id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right panel — Detail view */}
        <div className="flex-1 overflow-y-auto">
          {detail ? (
            <RaceDetailView detail={detail} />
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center max-w-md">
                <BookOpen className="w-12 h-12 text-[#F59E0B]/20 mx-auto mb-4" />
                <h3 className="text-white/40 font-heading text-lg mb-2">Select a race</h3>
                <p className="text-white/20 text-sm">
                  Every completed race is stored as an intelligence case — searchable by outcome, pattern, and signal.
                </p>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};


// ---- Race Case Card (sidebar) ----
const RaceCaseCard = ({ race, selected, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full text-left p-3 rounded-xl border transition-all duration-200 ${
      selected
        ? "border-[#F59E0B]/40 bg-[#F59E0B]/10"
        : "border-white/5 bg-white/[0.02] hover:border-white/10 hover:bg-white/[0.04]"
    }`}
    data-testid={`library-card-${race.race_id}`}
  >
    <div className="flex items-center justify-between mb-1.5">
      <div className="flex items-center gap-2">
        <MapPin className="w-3 h-3 text-[#F59E0B]" />
        <span className="text-white font-bold text-sm">{race.track_name}</span>
      </div>
      <ChevronRight className={`w-3 h-3 transition-colors ${selected ? "text-[#F59E0B]" : "text-white/20"}`} />
    </div>
    <div className="flex items-center gap-3 text-[10px] text-white/40 mb-2">
      <span>{race.distance_m}m</span>
      <span>{race.field_size} runners</span>
      <span>{race.race_shape} pace</span>
    </div>
    {/* Top label */}
    {race.top_labels?.best_run && (
      <div className="flex items-center gap-1.5 mb-2">
        <Trophy className="w-2.5 h-2.5 text-[#10B981]" />
        <span className="text-[#10B981] text-[10px] font-bold">{race.top_labels.best_run}</span>
      </div>
    )}
    {/* Tags */}
    <div className="flex items-center gap-1 flex-wrap">
      {race.tags?.slice(0, 4).map((tag) => (
        <span key={tag} className="px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider bg-white/5 text-white/30 border border-white/5">
          {tag}
        </span>
      ))}
    </div>
  </button>
);


// ---- Full Race Detail View ----
const RaceDetailView = ({ detail }) => {
  const { case: raceCase, events, stories } = detail;

  return (
    <div className="p-6 max-w-[900px] space-y-6 animate-in fade-in duration-300" data-testid="library-detail">
      {/* Why This Race Was Won */}
      <div className="rounded-2xl border-2 border-[#F59E0B]/30 bg-[#F59E0B]/5 p-5" data-testid="why-won-card">
        <div className="flex items-center gap-2 mb-3">
          <Trophy className="w-4 h-4 text-[#F59E0B]" />
          <span className="text-[#F59E0B] text-xs font-bold uppercase tracking-widest">Why This Race Was Won</span>
        </div>
        <p className="text-white text-base leading-relaxed">{raceCase.why_won}</p>
      </div>

      {/* Race context bar */}
      <div className="flex items-center gap-4 text-xs">
        <ContextChip label="Track" value={raceCase.track_name} />
        <ContextChip label="Distance" value={`${raceCase.distance_m}m`} />
        <ContextChip label="Pace" value={raceCase.race_shape} />
        <ContextChip label="Pressure" value={raceCase.pressure_profile} colour={raceCase.pressure_profile === "High" ? "#EF4444" : "#F59E0B"} />
        <ContextChip label="Bias" value={raceCase.bias_profile} />
      </div>

      {/* What Mattered */}
      {raceCase.what_mattered && (
        <div className="p-4 rounded-xl border border-[#F97316]/30 bg-[#F97316]/5">
          <div className="text-[#F97316] text-[10px] uppercase tracking-widest font-bold mb-1">What Mattered</div>
          <p className="text-white/80 text-sm leading-relaxed">{raceCase.what_mattered}</p>
        </div>
      )}

      {/* Replay Timeline */}
      <div data-testid="replay-timeline">
        <div className="flex items-center gap-2 mb-4">
          <Zap className="w-4 h-4 text-[#F97316]" />
          <span className="text-white font-heading text-sm font-bold uppercase tracking-wider">Replay Timeline</span>
        </div>

        {/* Visual track with event markers */}
        <div className="relative rounded-2xl border border-white/10 bg-[#0d0f12]/80 p-5 overflow-hidden">
          {/* Track line */}
          <div className="relative h-16 mb-4">
            <div className="absolute inset-x-0 top-1/2 h-px bg-white/10" />
            <div className="absolute left-0 top-1/2 -translate-y-1/2 text-white/15 text-[9px] font-mono">START</div>
            <div className="absolute right-0 top-1/2 -translate-y-1/2 text-white/15 text-[9px] font-mono">FINISH</div>

            {events.map((event, i) => {
              const maxDist = raceCase.distance_m || 2000;
              const pct = Math.max(5, Math.min(95, ((maxDist - event.distance_marker_m) / maxDist) * 100));
              const isTop = i % 2 === 0;
              return (
                <div
                  key={i}
                  className="absolute transition-all duration-300"
                  style={{ left: `${pct}%`, top: isTop ? "0" : "60%", transform: "translateX(-50%)" }}
                >
                  {/* Connector line */}
                  <div className="absolute left-1/2 w-px bg-white/10" style={{
                    top: isTop ? "100%" : "-100%",
                    height: isTop ? "calc(50% - 8px)" : "calc(50% - 8px)",
                    transform: "translateX(-50%)",
                  }} />
                  {/* Dot */}
                  <div className="w-2.5 h-2.5 rounded-full border-2 mx-auto" style={{
                    borderColor: event.colour,
                    backgroundColor: `${event.colour}30`,
                  }} />
                </div>
              );
            })}
          </div>

          {/* Event list */}
          <div className="space-y-2">
            {events.map((event, i) => (
              <div key={i} className="flex items-start gap-3 p-2.5 rounded-lg bg-white/[0.02] border border-white/5">
                <div className="w-1.5 h-full min-h-[32px] rounded-full flex-shrink-0 mt-0.5" style={{ backgroundColor: event.colour }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-white font-bold text-xs">{event.label}</span>
                    <span className="text-white/20 text-[10px] font-mono">{event.distance_marker_m}m to go</span>
                    {event.runner_numbers?.length > 0 && (
                      <span className="text-white/30 text-[10px]">
                        ({event.runner_numbers.map(n => `#${n}`).join(", ")})
                      </span>
                    )}
                  </div>
                  <p className="text-white/50 text-[11px] leading-relaxed">{event.explanation}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Runner Stories */}
      <div data-testid="runner-stories">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-4 h-4 text-[#3B82F6]" />
          <span className="text-white font-heading text-sm font-bold uppercase tracking-wider">Runner Stories</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {stories.map((story) => (
            <RunnerStoryCard key={story.no} story={story} />
          ))}
        </div>
      </div>

      {/* Field Summary Table */}
      <div data-testid="library-field-summary">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-white/40 text-xs font-bold uppercase tracking-wider">Full Field Intelligence</span>
        </div>
        <div className="rounded-xl border border-white/10 bg-[#0d0f12]/80 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-white/40 text-[10px] uppercase tracking-wider">
                <th className="px-3 py-2 text-left w-10">No</th>
                <th className="px-3 py-2 text-left">Horse</th>
                <th className="px-3 py-2 text-center w-10">Fin</th>
                <th className="px-3 py-2 text-center w-10">True</th>
                <th className="px-3 py-2 text-center w-16">Eff</th>
                <th className="px-3 py-2 text-center w-20">Mom</th>
                <th className="px-3 py-2 text-center w-20">Next</th>
              </tr>
            </thead>
            <tbody>
              {raceCase.field_summary?.map((h, i) => (
                <tr key={h.no} className={`border-b border-white/5 ${i % 2 === 0 ? "bg-white/[0.01]" : ""}`}>
                  <td className="px-3 py-1.5 text-white/60 font-mono text-xs">{h.no}</td>
                  <td className="px-3 py-1.5 text-white font-semibold text-xs">{h.horse}</td>
                  <td className="px-3 py-1.5 text-center text-white font-mono text-xs">{h.fin}</td>
                  <td className="px-3 py-1.5 text-center font-mono text-xs">
                    <span className={h.true < h.fin ? "text-[#10B981] font-bold" : h.true > h.fin ? "text-[#EF4444]" : "text-white/60"}>{h.true}</span>
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    <BandBadge value={h.eff} positive="Strong" negative="Weak" />
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    <BandBadge value={h.mom} positive="Building" negative="Fading" negativeAlt="Peaked Early" />
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    <BandBadge value={h.next} positive="Upgrade" negative="Downgrade" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


// ---- Runner Story Card ----
const RunnerStoryCard = ({ story }) => {
  const borderColour = story.next === "Upgrade" ? "#10B981" : story.next === "Downgrade" ? "#EF4444" : "rgba(255,255,255,0.1)";
  return (
    <div className="rounded-xl border bg-[#0d0f12]/80 p-3 overflow-hidden" style={{ borderColor: `${borderColour}40` }} data-testid={`story-${story.no}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-white font-bold text-sm">#{story.no} {story.horse}</span>
          <BandBadge value={story.next} positive="Upgrade" negative="Downgrade" />
        </div>
        <div className="text-white/30 text-[10px] font-mono">Fin {story.fin} / True {story.true}</div>
      </div>
      <div className="space-y-1 mb-2">
        <PhaseRow label="Early" text={story.early} />
        <PhaseRow label="Mid" text={story.mid} />
        <PhaseRow label="Late" text={story.late} />
      </div>
      <div className="pt-2 border-t border-white/5 text-white/60 text-[11px] leading-relaxed">{story.summary}</div>
    </div>
  );
};

const PhaseRow = ({ label, text }) => (
  <div className="flex items-start gap-2 text-[11px]">
    <span className="text-white/25 uppercase text-[9px] font-bold tracking-wider w-10 flex-shrink-0 pt-0.5">{label}</span>
    <span className="text-white/50 leading-relaxed">{text}</span>
  </div>
);

const ContextChip = ({ label, value, colour }) => (
  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/5 border border-white/5">
    <span className="text-white/30 text-[10px] uppercase">{label}</span>
    <span className="text-xs font-bold" style={{ color: colour || "white" }}>{value}</span>
  </div>
);

const BandBadge = ({ value, positive, negative, negativeAlt }) => {
  const isPositive = value === positive;
  const isNegative = value === negative || value === negativeAlt;
  const colour = isPositive ? "#10B981" : isNegative ? "#EF4444" : "#F59E0B";
  return (
    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{
      backgroundColor: `${colour}15`,
      color: colour,
    }}>{value}</span>
  );
};

export default RaceLibraryPage;
